<template>
	<article id="portfolio-project">
		<div class="container-med is-centered">
			<div class="grid-row grid-gutter-med">
				<div class="grid-col col-1-of-2">
					<router-link class="parent-link" to="/projects"><i class="fas fa-chevron-left" aria-hidden></i> Back to projects</router-link>
				</div>
				<div class="grid-col col-1-of-2 right-align">
					<router-link v-if="projects[0].id !== nextProjectId" class="parent-link" :to="{name: 'SingleProject', params: {projectName: nextProjectId}}">Next project <i class="fas fa-chevron-right" aria-hidden></i></router-link>
				</div>
			</div>
			<h1>{{currentProject.title}}</h1>
			<section id="project-screenshots">
				<ImageGallery ref="screenshots" :images="currentProject.images"></ImageGallery>
			</section>
			<section id="project-overview">
				<div class="grid-row grid-gutter-xl stack-mobile">
					<div class="grid-col col-1-of-3">
						<h4>Project Launch</h4>
						<p>{{currentProject.quickFacts.launch}}</p>
					</div>
					<div class="grid-col col-1-of-3">
						<h4>Project Duration</h4>
						<p>{{currentProject.quickFacts.developmentTime}}</p>
					</div>
					<div class="grid-col col-1-of-3">
						<h4>Main Purpose</h4>
						<p>{{currentProject.quickFacts.purpose}}</p>
					</div>
				</div>
			</section>
		</div>
		<div class="container-sm is-centered">
			<section id="project-details" v-html="currentProject.copy"></section>
			<div class="grid-row grid-gutter-med">
				<div class="grid-col col-1-of-2">
					<router-link class="parent-link" to="/projects"><i class="fas fa-chevron-left" aria-hidden></i> Back to projects</router-link>
				</div>
				<div class="grid-col col-1-of-2 right-align">
					<router-link v-if="projects[0].id !== nextProjectId" class="parent-link" :to="{name: 'SingleProject', params: {projectName: nextProjectId}}">Next project <i class="fas fa-chevron-right" aria-hidden></i></router-link>
				</div>
			</div>
		</div>
	</article>
</template>

<script>
import ImageGallery from '@/components/ImageGallery'	
export default {
	name: 'SingleProject',
	components: {
		ImageGallery
	},
	data() {
		return {
			projects: [
				{
					id: 'rl-hub',
					title: 'RL HUB',
					quickFacts: {
						launch: 'May 2016',
						techUsed: ['Salesforce', 'VueJS'],
						developmentTime: '2+ years (ongoing)',
						purpose: 'Client Engagement/Support'
					},
					images: [
						{
							'src': 'img/projects/screenshots/hub-screen-login.jpg',
							'alt': 'RL HUB login screen'
						},
						{
							'src': 'img/projects/screenshots/hub-screen-community.jpg',
							'alt': 'RL HUB Community screen'
						},
						{
							'src': 'img/projects/screenshots/hub-screen-community-mobile.jpg',
							'alt': 'RL HUB Community screen on a mobile device'
						},
						{
							'src': 'img/projects/screenshots/hub-screen-cases.jpg',
							'alt': 'RL HUB My Cases screen'
						},
						{
							'src': 'img/projects/screenshots/hub-screen-rewards.jpg',
							'alt': 'RL HUB Rewards screen'
						}
					],
					copy: `
						<h2>What is it?</h2>
						<p>RL HUB is a client engagement platform built for RL Solutions, primarily serving as a central location for all client interactions outside of the company's core products. The platform is composed of several components, each one covering a different aspect of the customer experience. These components include: an online community, product training, customer loyalty and rewards, and more.</p>
						<h2>What were the successes?</h2>
						<p>Despite having only two developers on this project, we quickly learned how to develop in the Salesforce architecture and we were able deploy the first major component within six months. While balancing the rest of our workload, we continued to deliver enhancements and four new major components to HUB over the next couple of years.</p>
						<p>By providing a forum for clients to speak to each other, they began to solve their own problems. This, in turn, reduced the number of incoming requests to our support team. Furthermore, it became easier to identify who our power users were, allowing us to leverage their knowledge in a more timely manner.</p>
						<h2>What were some of the challenges?</h2>
						<p>A project of this nature was fairly different from the short-lived projects that my team typically handled, more akin to a full product than a marketing initiative. We had to implement new tools to track and manage our deadlines, as well as stricter development and release cycles.</p>
						<p>It was a requirement for the project that all of the data be housed in Salesforce, which would prove to be both a blessing and a curse. Accessing that data required my team to build the application on Saleforce's proprietary platform and use their specific language. While building the application in these conditions provided certain benefits, such as security and hosting, their platform was antiquated and archaic. Restrictions on the structure of the application meant that we had to add unnecessary layers of complexity and bloat to the codebase in order to accomplish our goals. Finally, the existing documentation for the platform was sparse at best, which further slowed down development time.</p>
						<h2>How would you continue to improve it?</h2>
						<p>Recently, my team acquired a tool that would bi-directionally sync the data between Salesforce and a separate environment, allowing us to rebuild the application however we wanted. We chose to build it as a single page application, using <a href="https://vuejs.org/" target="_blank">VueJS</a> for the front end, and <a href="https://www.djangoproject.com/" target="_blank">Django</a> (a Python library) for the API driven back end. This version of the platform will be much leaner, allowing us to expose and leverage the data in other projects, and it will be overall more sustainable for future development.</p>
						<p>What's more, when the project was initially designed, it was as a desktop first application. However, since launch we noticed a growing number of mobile users. Because we are already going through the process of a complete rebuild, I am focusing on switching the pages to a mobile-first architecture to better support those users.</p>
					`
				},
				{
					id: 'khp-chatbot',
					title: 'Kids Help Phone Chatbot',
					quickFacts: {
						launch: 'November 2018',
						techUsed: ['jQuery'],
						developmentTime: '48 hours',
						purpose: 'Improving online chat module'
					},
					images: [
						{
							'src': 'img/projects/screenshots/khp-screen-data-tree.jpg',
							'alt': 'Chatbot data tree'
						},
						{
							'src': 'img/projects/screenshots/khp-screen-notes.jpg',
							'alt': 'Chatbot research/brainstorming on sticky notes'
						},
						{
							'src': 'img/projects/screenshots/khp-screen-1.jpg',
							'alt': 'Chatbot dialogue screen 1'
						},
						{
							'src': 'img/projects/screenshots/khp-screen-2.jpg',
							'alt': 'Chatbot dialogue screen 2'
						},
						{
							'src': 'img/projects/screenshots/khp-screen-3.jpg',
							'alt': 'Chatbot dialogue screen 3'
						},
						{
						  	'src': 'img/projects/screenshots/khp-screen-4.jpg',
							'alt': 'Chatbot dialogue screen 4'
						}
					],
					copy: `
						<h2>What is it?</h2>
						<p>The Kids Help Phone Chatbot was a project for <a href="https://www.hackworks.com/en/giftthecode2018" target="_blank">Gift the Code</a>, a hackathon done in partnership with various charities throughout the Greater Toronto Area. During the hackathon, groups were partnered with particular charities and had to solve one of that charity's problems. Our group partnered with Kids Help Phone and decided to focus on the challenge of improving their online chat module.</p>
						<p>Our solution involved using a chatbot to handle users before they actually spoke directly to a counselor. It would converse in human friendly language, triage users based on need and urgency, and provide activities to help users while they wait in line. Due to the time constraints of a hackathon, we knew it wouldn't be feasible to deliver a fully functioning product. Instead we focused on producing a simple prototype, created with thorough processes and research.</p>
						<h2>What were the successes?</h2>
						<p>Despite the large nature of our group (8 people) and the fact that most of us were strangers prior to this project, we were able to divide and delegate responsibilities extremely well, leveraging the different strengths of each member and maintaining a strong sense of cooperation. Each step we took was well planned, documented, and executed. This resulted in a strong supporting argument for each of our decisions and resulted in an airtight final prototype.</p>
						<p>The representative from Kids Help Phone was so impressed that they wanted to maintain contact with the group members, in order to have us present our prototype to other members of the charity at a follow-up date.</p>
						<h2>What were some of the challenges?</h2>
						<p>Due to our group's large size, we had too many developers for a simple prototype. In order to avoid skill overlap and to help fill the gaps, I switched between roles to cover various needs. Instead of doing pure development, I helped mentor the junior developers, assisted with product research, wrote humanized copy for the chatbot, and helped ensure code quality by finding and fixing code bugs.</p>
						<p>What's more, our team was composed primarily of working professionals, which meant managing the demands of our jobs and other time and energy constraints against the demands of creating a viable project.</p>
						<h2>How would you continue to improve it?</h2>
						<p>At the end of the day, what we produced was only a basic prototype; it would require a much larger infrastructure to properly support it. Aside from the coding, more research would have to be put into different user personas in order to identify suitable content and interactions to serve up.</p>
						<p>The beauty of the chatbot design is that it was based on a dialogue tree, so introducing new paths (branches) would be straightforward and could be done at any point in the project's life cycle. Once new content is ready, developers simply have to set up the branch that triggers it.</p>

					`
				},
				{
					id: 'holiday-jumble',
					title: 'Holiday Jumble',
					quickFacts: {
						launch: 'December 2016',
						techUsed: ['Phaser'],
						developmentTime: '2 weeks',
						purpose: 'Improving brand equity'
					},
					images: [
						{
							'src': 'img/projects/screenshots/jumble-screen-home.jpg',
							'alt': 'Holiday Jumble home screen'
						},
						{
							'src': 'img/projects/screenshots/jumble-screen-instructions.jpg',
							'alt': 'Holiday Jumble instruction screen'
						},
						{
							'src': 'img/projects/screenshots/jumble-screen-game-mobile.jpg',
							'alt': 'Holiday Jumble game screen on a mobile device'
						},
						{
							'src': 'img/projects/screenshots/jumble-screen-scoreboard.jpg',
							'alt': 'Holiday Jumble scoreboard screen'
						},
						{
							'src': 'img/projects/screenshots/jumble-screen-game-desktop.jpg',
							'alt': 'Holiday Jumble game screen on a desktop'
						}
					],
					copy: `
						<h2>What is it?</h2>
						<p>Holiday Jumble is a game I built for RL Solutions, as an alternative to the traditional Christmas cards that the company would send out to clients. The intent was to surprise and delight clients, thereby improving value and recognition of the brand.</p>
						<p>The project was done in partnership with the SickKids foundation. They provided additional exposure to our game through their social media channels, and RL Solutions donated money to the foundation based on the number of people that played the game. Because of this partnership, the game reached the general public in addition to our targeted client base.</p>
						<h2>What were the successes?</h2>
						<p>The game was an immediate hit, garnering several thousand plays within just a few days. We received plenty of positive feedback from clients and the general public alike. Moreover, the company was able to donate over $10,000 to the SickKids foundation, which helped foster a relationship with them over the next couple of years.</p>
						<h2>What were some of the challenges?</h2>
						<p>There are quite a few differences between traditional web development and game development. In order to build this project, I had to learn several new concepts, and I had to find and learn a suitable game engine; I ended up going with <a href="http://phaser.io/" target="_blank">Phaser</a>.</p>
						<p>Game development is intensely challenging, and the time constraint on the project contributed to the difficulty of this task. That said, the short time frame compelled me to keep scope creep to a minimum and to drill down on the core deliverables.</p>
						<p>Beyond the development of the game, I had to coordinate with many of my coworkers to get all the pieces required to make the game a fully fleshed experience. This added a layer of project management that I had to handle while simultaneously building the game.</p>
						<h2>How would you continue to improve it?</h2>
						<p>Quick turnaround on the project meant that I had to leave some features out. Most of these features were superfluous, such as additional power-ups. However, I feel that if they had been included, they would have increased the variety (and therefore the replay value) of the game, so I would have added them to later iterations.</p>
						<p>Another thing that fell by the wayside was security on the game leaderboard. Unfortunately, one of the realities of building a game client-side is that there is essentially no feasible way to fully secure the game, since users have full access to the code. Due to the time constraint, the only thing I could do was to add layers of obfuscation in the code, in order to deter the vast majority of people from making a hacking attempt. Given more time, I would continue to add layers of complexity to the obfuscation, as well as create server side checks to score validity to make hacking more difficult.</p>
					`
				}
			]
		}
	},
	created() {
		document.title = 'Blake Lipson | ' + this.currentProject.title;
	},
	computed: {
		currentProject() {
			const project = this.projects.filter(project => project.id === this.$route.params.projectName)
			return project[0]
		},
		nextProjectId() {
			let nextProjectIndex = this.projects.indexOf(this.currentProject) + 1;
			if (nextProjectIndex >= this.projects.length) {
				nextProjectIndex = 0;
			}
			return this.projects[nextProjectIndex].id;
		}
	},
	watch:{
		$route (to, from){
			document.title = 'Blake Lipson | ' + this.currentProject.title;
			window.scrollTo(0,0)
			this.$refs.screenshots.currentSlideIndex = 0
		}
	}
}
</script>

<style lang="scss">
@import 'src/assets/css/modules/all';
#portfolio-project {
	h1 {
		color: $navy;
		font-size: 2.25rem;
		margin-top: 0.25rem;
	}
	.parent-link {
		font-size: 0.875rem;
		font-weight: 500;
		&:hover, &:focus {
			text-decoration: underline;
		}
	}
}

#project-overview {
	background-color: #ebebeb;
	padding: 1.25rem;
	text-align: center;
	.grid-col {
		border-bottom: 2px solid #ccc;
		&:last-child {
			border-bottom: none;
		}
	}
	h4 {
		font-size: 1.125rem;
		font-weight: 500;
		margin: 0 0 0.25rem;
	}
	p {
		margin: 0;
	}
	@include gt-mobile {
		padding: 1.5rem 1rem;
		.grid-col {
			border-bottom: none;
			border-right: 2px solid #ccc;
			&:last-child {
				border-right: none;
			}
		}
		h4 {
			margin: 0 0 0.75rem;
		}
	}
}

#project-details {
	padding: 4rem 0;
	& > *:first-child {
		margin-top: 0;
	}
	& > *:last-child {
		margin-bottom: 0;
	}
	h2 {
		color: $teal;
		margin: 1.5rem 0 0.75rem;
		& + p {
			margin-top: 0;
		}
	}
	p {
		line-height: 1.8;
	}
	a {
		color: $teal;
		font-weight: 500;
		&:hover, &:focus {
			text-decoration: underline;
		}
	}
}
</style>